import { ReactElement, useMemo } from 'react';
import { LWDSubscription } from '@/api';
import { useCompanyContext } from '../providers';
import { useOpenSubscriptionPortalAction } from '../actions';
import { useFetchSubscriptionUsage } from '../queries';
import {
    UIMenuItemProps,
    UISection,
    UISectionContent,
    UISectionHeader,
    UIText
} from '@/ui';
import { blue, moonlight, red } from '@/ui/styles';
import { useFormatDate } from '@/common/hooks';
import { Trans } from 'react-i18next';
import { useCompaniesTranslation } from '../hooks';

type SubscriptionSettingsSectionProps = {
    subscription: LWDSubscription;
    canEdit: boolean;
};

export function LwdSubscriptionSettingsSection(
    props: SubscriptionSettingsSectionProps
): ReactElement {
    const { subscription, canEdit } = props;

    const { company } = useCompanyContext();

    // Authoritative addon usage from the server (devices with fall/tilt/geofence
    // effectively enabled), rather than counting devices on the client.
    const { usage } = useFetchSubscriptionUsage(company.id);
    const addonUsage = {
        fall: usage?.lwd?.fall?.used ?? 0,
        tilt: usage?.lwd?.tilt?.used ?? 0,
        geofence: usage?.lwd?.geofence?.used ?? 0
    };

    const manageSubscription = useOpenSubscriptionPortalAction({
        companyId: company.id
    });
    const viewInvoices = useOpenSubscriptionPortalAction({
        companyId: company.id
    });

    function onViewInvoices() {
        // Errors handled in the action
        viewInvoices.openSubscriptionPortal().catch(() => {});
    }

    function onManagePress() {
        // Errors handled in the action
        manageSubscription.openSubscriptionPortal().catch(() => {});
    }

    return (
        <LwdSubscriptionSettingsSectionForm
            subscription={subscription}
            addonUsage={addonUsage}
            onManagePress={onManagePress}
            onInvoiceHistoryPress={onViewInvoices}
            manageLoading={manageSubscription.loading}
            invoiceHistoryLoading={viewInvoices.loading}
            canEdit={canEdit}
        />
    );
}

type AddonUsage = {
    fall: number;
    tilt: number;
    geofence: number;
};

type LwdSubscriptionSettingsSectionFormProps = {
    subscription: LWDSubscription;
    addonUsage: AddonUsage;
    onManagePress: () => void;
    onInvoiceHistoryPress: () => void;
    manageLoading: boolean;
    invoiceHistoryLoading: boolean;
    canEdit: boolean;
};

function LwdSubscriptionSettingsSectionForm(
    props: LwdSubscriptionSettingsSectionFormProps
): ReactElement {
    const {
        subscription,
        addonUsage,
        onManagePress,
        onInvoiceHistoryPress,
        manageLoading,
        invoiceHistoryLoading,
        canEdit
    } = props;

    const { t } = useCompaniesTranslation();
    const { formatDateOnly } = useFormatDate();

    const title = t('components.lwd_subscription_settings_section.title');

    const subtitle = useMemo(() => {
        const periodEndStr = formatDateOnly(new Date(subscription.periodEnd));
        const periodStartStr = formatDateOnly(
            new Date(subscription.periodStart)
        );

        switch (subscription.status) {
            case 'active':
                if (subscription.cancelAtPeriodEnd) {
                    return t('shared.subscription.plan_expires_on', {
                        date: periodEndStr
                    });
                } else {
                    return t('shared.subscription.next_payment_due', {
                        date: periodEndStr
                    });
                }
            case 'trialing':
                if (subscription.cancelAtPeriodEnd) {
                    return t('shared.subscription.trial_expires_on', {
                        date: periodEndStr
                    });
                } else {
                    return t('shared.subscription.trial_renews_on', {
                        date: periodEndStr
                    });
                }
            case 'incomplete':
                return t('shared.subscription.status_incomplete');
            case 'past_due':
                return t('shared.subscription.past_due_since', {
                    date: periodStartStr
                });
            case 'paused':
                return t('shared.subscription.paused');
            case 'canceled':
            case 'incomplete_expired':
            case 'unpaid':
                return t('shared.subscription.status_terminated');
        }
    }, [
        formatDateOnly,
        subscription.cancelAtPeriodEnd,
        subscription.periodEnd,
        subscription.periodStart,
        subscription.status,
        t
    ]);

    const rightMenuItems: UIMenuItemProps[] = useMemo(() => {
        if (!canEdit) {
            return [];
        }
        const items: UIMenuItemProps[] = [
            {
                text: t(
                    'components.lwd_subscription_settings_section.view_subscription'
                ),
                leftIconName: 'sliders',
                loading: manageLoading,
                onClick: onManagePress
            },
            {
                text: t('shared.subscription.invoice_history'),
                leftIconName: 'list',
                loading: invoiceHistoryLoading,
                onClick: onInvoiceHistoryPress
            }
        ];

        return items;
    }, [
        manageLoading,
        onManagePress,
        invoiceHistoryLoading,
        onInvoiceHistoryPress,
        canEdit,
        t
    ]);

    const periodEndStr = formatDateOnly(new Date(subscription.periodEnd));

    return (
        <UISection>
            <UISectionHeader
                title={title}
                subtitle={subtitle}
                rightMenuItemsText={t('shared.labels.more')}
                rightMenuItems={rightMenuItems}
            />
            {subscription.status === 'unpaid' && (
                <UISectionContent borderBottom>
                    <div style={{ display: 'flex' }}>
                        <UIText>
                            <Trans
                                ns="companies"
                                i18nKey="shared.subscription.unpaid_notice"
                                values={{
                                    status: t(
                                        'shared.subscription.status_unpaid'
                                    )
                                }}
                                components={{
                                    b: (
                                        <b>
                                            <span style={{ color: red }} />
                                        </b>
                                    )
                                }}
                            />
                        </UIText>
                    </div>
                </UISectionContent>
            )}
            {subscription.status === 'trialing' && (
                <UISectionContent borderBottom>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <UIText>
                            <Trans
                                ns="companies"
                                i18nKey="components.lwd_subscription_settings_section.trialing_notice"
                                values={{
                                    status: t(
                                        'components.lwd_subscription_settings_section.status_trialling'
                                    )
                                }}
                                components={{
                                    b: (
                                        <b>
                                            <span style={{ color: blue }} />
                                        </b>
                                    )
                                }}
                            />
                        </UIText>
                    </div>
                </UISectionContent>
            )}
            {subscription.status !== 'trialing' &&
                subscription.cancelAtPeriodEnd && (
                    <UISectionContent borderBottom>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <UIText>
                                <Trans
                                    ns="companies"
                                    i18nKey="shared.subscription.expiring_notice"
                                    values={{ date: periodEndStr }}
                                    components={{
                                        a: (
                                            <a
                                                href="https://www.safepointapp.com/contact"
                                                style={{
                                                    color: blue,
                                                    textDecoration: 'underline'
                                                }}
                                            />
                                        )
                                    }}
                                />
                            </UIText>
                        </div>
                    </UISectionContent>
                )}

            {subscription.status !== 'trialing' && (
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>
                                {t(
                                    'components.lwd_subscription_settings_section.device_count'
                                )}
                            </UIText>
                            <UIText type="default2">
                                {`${subscription.quantity} ${subscription.quantity === 1 ? 'device' : 'devices'}`}
                            </UIText>
                        </div>

                        {subscription.fallQuantity > 0 && (
                            <AddonUsageRow
                                label={t(
                                    'components.lwd_subscription_settings_section.addon_fall'
                                )}
                                used={addonUsage.fall}
                                total={subscription.fallQuantity}
                            />
                        )}
                        {subscription.tiltQuantity > 0 && (
                            <AddonUsageRow
                                label={t(
                                    'components.lwd_subscription_settings_section.addon_tilt'
                                )}
                                used={addonUsage.tilt}
                                total={subscription.tiltQuantity}
                            />
                        )}
                        {subscription.geoQuantity > 0 && (
                            <AddonUsageRow
                                label={t(
                                    'components.lwd_subscription_settings_section.addon_geofence'
                                )}
                                used={addonUsage.geofence}
                                total={subscription.geoQuantity}
                            />
                        )}

                        {canEdit && (
                            <div>
                                <UIText>
                                    <a
                                        href="https://www.safepointapp.com/hardware/lone-worker-device-with-sim"
                                        target="_blank"
                                        style={{
                                            color: blue,
                                            textDecoration: 'underline'
                                        }}
                                    >
                                        {t(
                                            'components.lwd_subscription_settings_section.buy_more'
                                        )}
                                    </a>
                                </UIText>
                            </div>
                        )}
                    </div>
                </UISectionContent>
            )}
        </UISection>
    );
}

function AddonUsageRow(props: {
    label: string;
    used: number;
    total: number;
}): ReactElement {
    const { label, used, total } = props;

    const overLimit = used > total;
    const percentage = Math.min(100, Math.max(0, (used / total) * 100));
    const fillColor = overLimit ? red : blue;
    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                gap: 4
            }}
        >
            <UIText>{label}</UIText>
            <UIText type="default2">
                <span style={overLimit ? { color: red } : undefined}>
                    {`${used} of ${total} used`}
                </span>
            </UIText>
            <div
                style={{
                    height: 6,
                    maxHeight: 6,
                    width: '100%',
                    backgroundColor: moonlight,
                    borderRadius: 3
                }}
            >
                <div
                    style={{
                        height: 6,
                        maxHeight: 6,
                        width: `${percentage}%`,
                        backgroundColor: fillColor,
                        borderRadius: 3
                    }}
                />
            </div>
        </div>
    );
}
